import React from 'react';
import { ArrowRight, BookOpen, GraduationCap, Award, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-700">
        {/* Animated Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 via-transparent to-cyan-500/20 animate-pulse"></div>
        
        {/* Geometric Shapes */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-float" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="text-center max-w-5xl mx-auto">
          {/* Decorative Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-8 animate-slideInLeft">
            <Sparkles className="text-cyan-300" size={18} />
            <span className="text-white/90 text-sm font-medium">Distinguished Professor & Academic Leader</span>
          </div>

          {/* Main Heading with Gradient Text */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 animate-fadeIn">
            <span className="block text-white mb-2">Dr. Ayaz Afsar</span>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-blue-300 to-cyan-400">
              Scholar. Leader. Innovator.
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-blue-100 mb-4 font-light animate-slideInRight">
            Professor of English | Applied Linguist | Academic Leader
          </p>

          {/* Tagline */}
          <p className="text-lg md:text-xl text-white/80 mb-12 max-w-3xl mx-auto leading-relaxed animate-fadeIn" style={{animationDelay: '0.2s'}}>
            Advancing language scholarship, academic leadership, and meaningful education through three decades of excellence in research, teaching, and institutional development.
          </p>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 max-w-4xl mx-auto animate-fadeIn" style={{animationDelay: '0.4s'}}>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4 hover:bg-white/15 transition-all duration-300">
              <div className="text-3xl font-bold text-cyan-300 mb-1">30+</div>
              <div className="text-sm text-white/80">Years Experience</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4 hover:bg-white/15 transition-all duration-300">
              <div className="text-3xl font-bold text-cyan-300 mb-1">100+</div>
              <div className="text-sm text-white/80">PhD Students</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4 hover:bg-white/15 transition-all duration-300">
              <div className="text-3xl font-bold text-cyan-300 mb-1">50+</div>
              <div className="text-sm text-white/80">Publications</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4 hover:bg-white/15 transition-all duration-300">
              <div className="text-3xl font-bold text-cyan-300 mb-1">VP</div>
              <div className="text-sm text-white/80">Academic Leadership</div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fadeIn" style={{animationDelay: '0.6s'}}>
            <Button
              onClick={() => scrollToSection('about')}
              size="lg"
              className="bg-white text-blue-900 hover:bg-cyan-50 px-8 py-6 text-lg rounded-xl transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 font-semibold group"
            >
              Explore My Work
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
            <Button
              onClick={() => scrollToSection('publications')}
              variant="outline"
              size="lg"
              className="border-2 border-white/30 text-white hover:bg-white/10 backdrop-blur-sm px-8 py-6 text-lg rounded-xl transition-all duration-300 hover:border-white/50 font-semibold group"
            >
              <BookOpen className="mr-2 group-hover:scale-110 transition-transform" size={20} />
              View Publications
            </Button>
          </div>

          {/* Floating Achievement Badges */}
          <div className="hidden lg:block">
            <div className="absolute left-10 top-1/3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 max-w-xs animate-float">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center">
                  <Award className="text-white" size={24} />
                </div>
                <div className="text-left">
                  <div className="text-white font-semibold">Vice President</div>
                  <div className="text-white/70 text-sm">Academics, IIUI</div>
                </div>
              </div>
            </div>

            <div className="absolute right-10 top-1/2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 max-w-xs animate-float" style={{animationDelay: '1.5s'}}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl flex items-center justify-center">
                  <GraduationCap className="text-white" size={24} />
                </div>
                <div className="text-left">
                  <div className="text-white font-semibold">PhD & MPhil</div>
                  <div className="text-white/70 text-sm">University of Manchester</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
